import { READ_INCREMENTAL_STUDENT_LIST, READ_STUDENT_LIST } from 'network/shared/student';
import type { StudentT } from 'types/shared/core/Student';
import type { EmptyRequestT } from 'types/shared/reqAndRes/CommonRequest';
import type { MyResponse } from 'types/shared/reqAndRes/MyResponse';
import type {
  ReadIncrementalStudentListRequestT,
  ReadIncrementalStudentListResponseT,
} from 'types/shared/reqAndRes/Student';
import { api } from 'utils/api/api';
import Token from 'utils/Token';
import StudentListCacheDB, { StudentListCacheScopeT } from './StudentListCacheDB';

// 로컬 sync 이력이 없을 때 모든 학생을 변경분으로 받기 위한 최초 기준 시각이다.
const INITIAL_SYNCED_AT = new Date(0).toISOString();
export const REQUIRED_STUDENT_THRESHOLD = 100;

export type StudentListCacheLoadResultT = {
  studentList: StudentT[];
  mode: string;
  requestSequence: string[];
  updatedStudentCount: number;
  unchangedStudentCount: number;
  requiredStudentCount: number;
  authoritativeStudentIds: number[];
  error: Error | null;
};

type StudentListCacheLoadOptionsT = {
  traditionalStudentList?: readonly StudentT[];
  onPhase?: (phase: string) => void;
  onLog?: (message: string) => void;
};

// 증분 API와 암호화 IndexedDB를 조합해 현재 권한 범위의 최종 학생 목록을 만든다.
export default class StudentListCacheLoader {
  readonly cacheDB: StudentListCacheDB;

  constructor(cacheDB = new StudentListCacheDB()) {
    this.cacheDB = cacheDB;
  }

  // 현재 access token의 교사 user/academy 식별자로 IndexedDB scope를 만든다.
  getCurrentScope = (): StudentListCacheScopeT | null => {
    const teacherId = Token.getCurrentUserId();
    const academyId = Token.getCurrentTeacherAcademyId();
    if (!teacherId || !academyId) return null;
    return { academyId, teacherId };
  };

  // 캐시 복구, 증분 반영, 누락 보충 및 필요한 full sync를 거쳐 최종 목록을 반환한다.
  load = async (
    scope: StudentListCacheScopeT,
    options: StudentListCacheLoadOptionsT = {},
  ): Promise<StudentListCacheLoadResultT> => {
    const requestSequence: string[] = [];
    const traditionalStudentList = options.traditionalStudentList
      ? this.copyStudentList(options.traditionalStudentList)
      : null;

    if (!StudentListCacheDB.isSupported()) {
      if (!traditionalStudentList) requestSequence.push('cacheFull');
      const studentList =
        traditionalStudentList || (await this.readFullStudentList(scope, options));
      return this.buildFallbackResult(studentList, 'full only: 캐시 미지원', null, requestSequence);
    }

    try {
      // 같은 academy/teacher의 다른 화면은 API 조회부터 최종 캐시 읽기까지 순서대로 실행한다.
      return await this.cacheDB.withScopeLock(scope, async (scopeLock) => {
        const oldMetadata = await this.cacheDB.readScopeMetadata(scope, scopeLock);
        const lastSyncedAt = oldMetadata?.lastSyncedAt || INITIAL_SYNCED_AT;

        options.onPhase?.('증분 학생 목록 조회 중');
        requestSequence.push('incremental');
        let incrementalResult = await this.readIncrementalStudentList(
          scope,
          lastSyncedAt,
          undefined,
          options,
        );
        let authoritativeStudentIds = this.getAuthoritativeStudentIds(incrementalResult);
        let requiredStudentCount = 0;
        let shouldFullSync = !oldMetadata;
        let mode = oldMetadata ? 'incremental sync' : 'initial full sync';

        if (oldMetadata) {
          options.onPhase?.('로컬 DEK 복원 중');
          const unlockResult = await this.cacheDB.unlockScope(
            scope,
            incrementalResult.kek,
            incrementalResult.pkek,
            scopeLock,
          );

          if (!unlockResult) {
            shouldFullSync = true;
            mode = 'full sync: DEK 복원 실패';
          } else {
            // 새 권한 학생이나 손실된 row가 id-only로 오면 full payload를 한 번 더 요청한다.
            const missingStudentIds = await this.cacheDB.findMissingStudentIds(
              scope,
              incrementalResult.unchangedStudentIds,
              scopeLock,
            );

            if (!missingStudentIds) {
              shouldFullSync = true;
              mode = 'full sync: 캐시 손상';
            } else if (missingStudentIds.length > REQUIRED_STUDENT_THRESHOLD) {
              requiredStudentCount = missingStudentIds.length;
              shouldFullSync = true;
              mode = `full sync: 로컬 miss ${missingStudentIds.length}명`;
            } else if (missingStudentIds.length > 0) {
              requiredStudentCount = missingStudentIds.length;
              options.onPhase?.(`누락 학생 ${missingStudentIds.length}명 보충 조회 중`);
              requestSequence.push(`requiredStudents(${missingStudentIds.length})`);
              incrementalResult = await this.readIncrementalStudentList(
                scope,
                lastSyncedAt,
                missingStudentIds,
                options,
              );
              authoritativeStudentIds = this.getAuthoritativeStudentIds(incrementalResult);
            }
          }
        }

        if (shouldFullSync) {
          options.onPhase?.('전체 학생 목록으로 IndexedDB 교체 중');
          // syncedAt보다 먼저 받은 비교용 목록을 저장하면 요청 사이의 변경을 놓칠 수 있다.
          requestSequence.push('cacheFull');
          const fullStudentList = await this.readFullStudentList(scope, options);
          await this.cacheDB.replaceScope(
            scope,
            incrementalResult.kek,
            fullStudentList,
            incrementalResult.syncedAt,
            scopeLock,
          );
        } else {
          options.onPhase?.('증분 결과를 IndexedDB에 반영 중');
          const applied = await this.cacheDB.applyIncrementalSync(
            scope,
            incrementalResult.updatedStudents,
            authoritativeStudentIds,
            incrementalResult.syncedAt,
            scopeLock,
          );

          if (!applied) {
            mode = 'full sync: 증분 반영 실패';
            requestSequence.push('cacheFull');
            const fullStudentList = await this.readFullStudentList(scope, options);
            await this.cacheDB.replaceScope(
              scope,
              incrementalResult.kek,
              fullStudentList,
              incrementalResult.syncedAt,
              scopeLock,
            );
          }
        }

        options.onPhase?.('암호화 학생 목록 복호화 중');
        const cachedStudentList = await this.cacheDB.readStudents(scope, scopeLock);
        if (!cachedStudentList) throw new Error('암호화 학생 목록을 복호화하지 못했습니다.');

        return {
          studentList: cachedStudentList,
          mode,
          requestSequence,
          updatedStudentCount: incrementalResult.updatedStudents.length,
          unchangedStudentCount: incrementalResult.unchangedStudentIds.length,
          requiredStudentCount,
          authoritativeStudentIds,
          error: null,
        };
      });
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      options.onLog?.(`캐시 로딩 실패: ${normalizedError.message}`);
      if (!traditionalStudentList) requestSequence.push('cacheFull');
      const studentList =
        traditionalStudentList || (await this.readFullStudentList(scope, options));
      return this.buildFallbackResult(
        studentList,
        'full only: 캐시 로딩 실패',
        normalizedError,
        requestSequence,
      );
    }
  };

  // 메모리에만 있는 평문 DEK 참조를 제거한다.
  lock = (): void => {
    this.cacheDB.lock();
  };

  // 기존 전체 학생 목록 API를 full-sync fallback으로 호출한다.
  private readFullStudentList = async (
    scope: StudentListCacheScopeT,
    options: StudentListCacheLoadOptionsT,
  ): Promise<StudentT[]> => {
    options.onLog?.('GET /student 전체 학생 목록 요청');
    const response = await api<EmptyRequestT, MyResponse<StudentT>>(
      READ_STUDENT_LIST(scope.academyId),
      {},
    );
    if (!response.isSuccess || !response.objects) {
      throw new Error('기존 전체 학생 목록 API가 실패했습니다.');
    }
    options.onLog?.(`전체 학생 목록 응답: ${response.objects.length}명`);
    return response.objects;
  };

  // 현재 watermark 이후 변경분과 authoritative ID 집합을 조회한다.
  private readIncrementalStudentList = async (
    scope: StudentListCacheScopeT,
    lastSyncedAt: string,
    requiredStudentIds: number[] | undefined,
    options: StudentListCacheLoadOptionsT,
  ): Promise<ReadIncrementalStudentListResponseT> => {
    const request: ReadIncrementalStudentListRequestT = {
      lastSyncedAt,
      ...(requiredStudentIds?.length ? { requiredStudentIds } : {}),
    };
    options.onLog?.(`GET /student/incremental 요청: required=${requiredStudentIds?.length || 0}명`);
    const response = await api<
      ReadIncrementalStudentListRequestT,
      MyResponse<ReadIncrementalStudentListResponseT>
    >(READ_INCREMENTAL_STUDENT_LIST(scope.academyId), request);
    if (!response.isSuccess || !response.object) {
      throw new Error('증분 학생 목록 API가 실패했습니다.');
    }
    options.onLog?.(
      `증분 학생 목록 응답: full ${response.object.updatedStudents.length}명, id-only ${response.object.unchangedStudentIds.length}명`,
    );
    return response.object;
  };

  // 증분 응답의 full payload와 id-only를 현재 권한의 ID 집합으로 합친다.
  private getAuthoritativeStudentIds = (
    incrementalResult: ReadIncrementalStudentListResponseT,
  ): number[] => [
    ...incrementalResult.unchangedStudentIds,
    ...incrementalResult.updatedStudents.map((student) => student.id),
  ];

  // 화면 Store가 학생 객체에 파생 필드를 붙여도 비교 기준 payload가 변하지 않게 복사한다.
  private copyStudentList = (studentList: readonly StudentT[]): StudentT[] =>
    studentList.map((student) => ({ ...student }));

  // 캐시를 사용할 수 없을 때도 기존 전체조회 결과를 동일한 반환 계약으로 감싼다.
  private buildFallbackResult = (
    studentList: StudentT[],
    mode: string,
    error: Error | null = null,
    requestSequence: string[] = [],
  ): StudentListCacheLoadResultT => ({
    studentList,
    mode,
    requestSequence,
    updatedStudentCount: 0,
    unchangedStudentCount: 0,
    requiredStudentCount: 0,
    authoritativeStudentIds: studentList.map((student) => student.id),
    error,
  });
}
